import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useFlash } from "../lib/flash";
import { formatKr, fmtSlots, slotsOf, fmtDateTime, fmtTime, relTime } from "../lib/format";
import { Pill, PriceTag, StockBar, Thumb, EventText } from "../components/bits";
import StackTable from "../components/StackTable";
import OfferMenu from "../components/OfferMenu";
import CommentReminderBox from "../components/CommentReminderBox";
import { CopyPlus } from "lucide-react";
import { useAuth, canSimulate } from "../lib/auth";

function RepostText({ text }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const long = text.length > 160;
  return (
    <div className="repost-text" data-testid="offer-repost-text-view">
      <span className="muted">Tekst til genopslag:</span> <span style={{ whiteSpace: "pre-line" }}>{long && !open ? text.slice(0, 160).trimEnd() + "…" : text}</span>
      {long && (
        <button type="button" className="linklike" onClick={() => setOpen(!open)} data-testid="offer-repost-text-toggle">
          {open ? "Vis mindre" : "Vis mere"}
        </button>
      )}
    </div>
  );
}

function OrderActions({ o, act }) {
  const B = (action, label, cls = "") => (
    <button className={`btn small ${cls}`} onClick={() => act(o.id, action)} data-testid={`order-${action}-${o.id}`}>
      {label}
    </button>
  );
  if (o.status === "reserved") return (<>{B("pickup", "Udleveret", "primary")} {B("cancel", "Annuller", "danger")}</>);
  if (o.status === "pending_confirm") return (<>{B("confirm", "Bekræft")} {B("pickup", "Udleveret", "primary")} {B("cancel", "Annuller", "danger")}</>);
  if (o.status === "picked_up") return B("undo", "Fortryd");
  if (o.status === "no_show") return B("pickup", "Hentet alligevel");
  if (o.status === "waitlist") return B("cancel", "Fjern", "danger");
  return null;
}

function VariantTable({ label, rows }) {
  return (
    <section className="panel" data-testid="variant-table">
      <h2>{label || "Varianter"}</h2>
      <div className="table-wrap">
        <StackTable>
          <thead><tr><th>{label || "Variant"}</th><th className="num">Antal</th><th className="num">Reserveret</th><th className="num">Udleveret</th><th className="num">Ledige</th></tr></thead>
          <tbody>
            {rows.map((v) => (
              <tr key={v.id} data-testid={`variant-stat-${v.id}`}>
                <td data-label={label || "Variant"}><b>{v.name}</b>{v.waitlist ? <span className="muted"> · {v.waitlist} på venteliste</span> : null}</td>
                <td className="num" data-label="Antal">{v.qty}</td>
                <td className="num" data-label="Reserveret">{v.reserved}</td>
                <td className="num" data-label="Udleveret">{v.picked_up}</td>
                <td className="num" data-label="Ledige">{v.available === 0 ? <span className="pill no_show">Udsolgt</span> : v.available}</td>
              </tr>
            ))}
          </tbody>
        </StackTable>
      </div>
    </section>
  );
}

export default function OfferDetail() {
  const { store } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const flash = useFlash();
  const [d, setD] = useState(null);
  const [stock, setStock] = useState("");
  const [manual, setManual] = useState({ name: "", phone: "", qty: 1 });

  const load = useCallback(async () => {
    const r = await api.get(`/offers/${id}`);
    setD(r.data);
    setStock(r.data.stats.stock_total);
  }, [id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  if (!d) return <div className="loading">Indlæser…</div>;
  const { offer, stats, state } = d;

  const post = async (url, body) => {
    try {
      const r = await api.post(url, body);
      if (r.data.flash) flash(r.data.flash);
      return r.data;
    } catch (err) {
      flash(apiError(err.response?.data?.detail) || err.message, "error");
    }
  };

  const act = async (oid, action) => {
    await post(`/orders/${oid}/${action}`);
    load();
  };

  const updateStock = async (e) => {
    e.preventDefault();
    await post(`/offers/${offer.id}/stock`, { stock_total: Number(stock) });
    load();
  };

  const addManual = async (e) => {
    e.preventDefault();
    if (!manual.name.trim()) return;
    await post(`/offers/${offer.id}/manual-order`, { ...manual, qty: Number(manual.qty) });
    setManual({ name: "", phone: "", qty: 1 });
    load();
  };

  const simpleAction = async (url, confirmMsg, redirect) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    const r = await post(url);
    if (redirect) nav(redirect);
    else load();
    return r;
  };

  const del = async () => {
    if (!window.confirm("Slet tilbuddet? Det er ikke slået op endnu.")) return;
    try {
      const r = await api.delete(`/offers/${offer.id}`);
      flash(r.data.flash);
      nav("/overblik");
    } catch (err) {
      flash(apiError(err.response?.data?.detail) || err.message, "error");
    }
  };

  const downloadCsv = async () => {
    const r = await api.get(`/offers/${offer.id}/export`);
    const blob = new Blob([r.data.content], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = r.data.filename;
    a.click();
  };

  const row = (o) => (
    <tr className={o.status === "picked_up" ? "done" : ""} key={o.id}>
      <td>
        {o.name}
        {o.no_show_count ? <span className="pill no_show" title="Tidligere udeblivelser"> {o.no_show_count} udebl.</span> : null}
        {o.source === "manual" ? <span className="muted"> (manuel)</span> : null}
        {o.phone ? " 📱" : ""}
        {!store?.staff && o.comment_reminder === "sent" && o.comment_reminder_sent_at && (
          <span className="vn-tag cr-tag" data-testid={`order-reminded-${o.id}`}>Påmindet i kommentar kl. {fmtTime(o.comment_reminder_sent_at)}</span>
        )}
      </td>
      <td>
        <span className="code">{o.code}</span>
      </td>
      <td className="num">
        {o.qty}
        {o.requested_qty > o.qty && o.status !== "waitlist" ? <span className="muted">/{o.requested_qty}</span> : null}
        {o.variant_name && <span className="vn-tag" data-testid={`order-variant-${o.id}`}>{o.variant_name}</span>}
      </td>
      <td>
        <Pill status={o.status} />
        {o.status === "pending_confirm" && o.confirm_deadline ? (
          <>
            <br />
            <small>senest {fmtDateTime(o.confirm_deadline)}</small>
          </>
        ) : null}
      </td>
      <td>
        <small>{relTime(o.created_at)}</small>
      </td>
      <td className="num" style={{ whiteSpace: "nowrap" }}>
        <OrderActions o={o} act={act} />
      </td>
    </tr>
  );

  return (
    <>
      <div className="page-head">
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <Thumb offer={offer} />
          <div>
            <h1>
              {offer.title}
              {!store?.staff && <OfferMenu offer={offer} onlyDuplicate />}
            </h1>
            <p>
              <Pill status={state.cls} label={state.label} />
              {offer.copy_pending ? <> <Pill status="draft" label="Kopi" /></> : null}
              {offer.published_test ? <> <Pill status="draft" label="Test" /></> : null} Afhentning{" "}
              {fmtSlots(slotsOf(offer))}
              {offer.sales_start
                ? ` · bestil ${fmtDateTime(offer.sales_start)}${offer.sales_end ? "–" + fmtDateTime(offer.sales_end) : ""}`
                : offer.sales_end
                ? ` · bestil senest ${fmtDateTime(offer.sales_end)}`
                : ""}
            </p>
          </div>
        </div>
        <div className="actions">
          {offer.status === "draft" && (
            <button className="btn jatak" onClick={() => simpleAction(`/offers/${offer.id}/publish`)} data-testid="publish-btn">
              Slå op på Facebook
            </button>
          )}
          {offer.status === "scheduled" && (
            <>
              <button className="btn" onClick={() => simpleAction(`/offers/${offer.id}/unschedule`)}>
                Annuller planlægning
              </button>
              <button className="btn jatak" onClick={() => simpleAction(`/offers/${offer.id}/publish`, "Slå op nu i stedet for det planlagte tidspunkt?")}>
                Slå op nu
              </button>
            </>
          )}
          {!!offer.published_test && canSimulate(store) && (
            <Link className="btn" to={`/simulator?offer=${offer.id}`} data-testid="open-simulator">
              Åbn i simulator
            </Link>
          )}
          <Link className="btn" to={`/offers/${offer.id}/picklist`}>
            Plukliste
          </Link>
          <Link className="btn" to={`/offers/${offer.id}/edit`} data-testid="edit-offer">
            Rediger
          </Link>
        </div>
      </div>
      {offer.status === "scheduled" && (
        <div className="notice">
          Planlagt: opslaget kommer på Facebook <b>{fmtDateTime(offer.publish_at)}</b>. Du kan stadig rette det indtil da.
        </div>
      )}
      {(offer.repost_n || (offer.repost_enabled && !store?.staff)) && (
        <div className="notice" data-testid="offer-repost-info">
          {offer.repost_n ? <><b>Genopslag {offer.repost_n}/3</b> af «{offer.repost_of_title}». </> : null}
          {offer.repost_enabled && !store?.staff ? (
            <>Læg op igen, hvis der er varer tilbage: <b>{offer.repost_days} {offer.repost_days === 1 ? "dag" : "dage"}</b> efter sidste afhentningsdag – du godkender selv forslaget.
              <RepostText text={offer.repost_text} /></>
          ) : null}
        </div>
      )}
      {!store?.staff && (
        <CommentReminderBox r={d.comment_reminders}
          onToggle={async (on) => { await post(`/offers/${offer.id}/comment-reminder`, { on }); load(); }} />
      )}
      <div className="grid cols-main">
        <div>
          {stats.variants && <VariantTable label={offer.variant_label} rows={stats.variants} />}
          <section className="panel">
            <h2>
              Reservationer <span className="muted" style={{ fontWeight: 400 }}>· {stats.open_orders} åbne</span>
            </h2>
            {d.orders.length ? (
              <div className="table-wrap">
                <StackTable>
                  <thead>
                    <tr>
                      <th>Kunde</th>
                      <th>Kode</th>
                      <th className="num">Antal</th>
                      <th>Status</th>
                      <th>Bestilt</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>{d.orders.map(row)}</tbody>
                </StackTable>
              </div>
            ) : (
              <div className="empty">
                <p>
                  {offer.status === "scheduled"
                    ? "Reservationerne kommer her, når opslaget er på Facebook."
                    : offer.status === "draft"
                    ? 'Slå tilbuddet op for at modtage "ja tak".'
                    : "Ingen har sagt ja tak endnu."}
                </p>
              </div>
            )}
          </section>
          {d.waitlist.length > 0 && (
            <section className="panel">
              <h2>Venteliste</h2>
              <p className="muted">Rykker automatisk op, når nogen melder afbud, eller du lægger flere på lager.</p>
              <div className="table-wrap">
                <StackTable>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Kunde</th>
                      <th className="num">Ønsker</th>
                      <th>Siden</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.waitlist.map((o, i) => (
                      <tr key={o.id}>
                        <td>{i + 1}</td>
                        <td>{o.name}</td>
                        <td className="num">{o.requested_qty}</td>
                        <td>
                          <small>{relTime(o.created_at)}</small>
                        </td>
                        <td className="num">
                          <OrderActions o={o} act={act} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </StackTable>
              </div>
            </section>
          )}
          {d.gone.length > 0 && (
            <details className="panel">
              <summary>
                <b>Annulleret og udløbet ({d.gone.length})</b>
              </summary>
              <div className="table-wrap" style={{ marginTop: 10 }}>
                <StackTable>
                  <tbody>
                    {d.gone.map((o) => (
                      <tr className="done" key={o.id}>
                        <td>{o.name}</td>
                        <td className="num">{o.qty || o.requested_qty}</td>
                        <td>
                          <Pill status={o.status} />
                        </td>
                        <td>
                          <small>{relTime(o.updated_at)}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </StackTable>
              </div>
            </details>
          )}
        </div>
        <aside>
          <section className="panel">
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <PriceTag offer={offer} big />
              <div>
                <small className="muted">Omsat</small> <b>{formatKr(d.revenue)}</b>
              </div>
            </div>
            <StockBar stats={stats} />
            <form onSubmit={updateStock} className="actions" style={{ marginTop: 14 }}>
              <label htmlFor="stock" style={{ margin: 0 }}>
                Lager
              </label>
              <input id="stock" type="number" min={stats.held} value={stock} onChange={(e) => setStock(e.target.value)} style={{ width: 90 }} data-testid="stock-input" />
              <button className="btn small" data-testid="stock-update">
                Opdater
              </button>
            </form>
            <p className="muted" style={{ fontSize: 13, margin: "6px 0 0" }}>
              Flere på lager rykker ventelisten op med det samme.
            </p>
          </section>
          {["live", "closed"].includes(offer.status) && !offer.noshows_processed && (
            <section className="panel">
              <h2>Tilføj ordre manuelt</h2>
              <p className="muted" style={{ fontSize: 13 }}>
                Til kunder der ringer eller spørger i butikken.
              </p>
              <form onSubmit={addManual}>
                <div className="field">
                  <label htmlFor="m-name">Navn</label>
                  <input id="m-name" required value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} data-testid="manual-name" />
                </div>
                <div className="row">
                  <div className="field">
                    <label htmlFor="m-phone">Mobil</label>
                    <input id="m-phone" type="tel" value={manual.phone} onChange={(e) => setManual({ ...manual, phone: e.target.value })} placeholder="Til SMS-påmindelse" />
                  </div>
                  <div className="field">
                    <label htmlFor="m-qty">Antal</label>
                    <input id="m-qty" type="number" min={1} value={manual.qty} onChange={(e) => setManual({ ...manual, qty: e.target.value })} />
                  </div>
                </div>
                <button className="btn primary" data-testid="manual-submit">
                  Reserver
                </button>
              </form>
            </section>
          )}
          <section className="panel">
            <div className="actions">
              <button className="btn small" onClick={downloadCsv}>
                Eksportér CSV
              </button>
              {offer.status !== "draft" && (
                <button className="btn small" onClick={() => post(`/offers/${offer.id}/duplicate`).then((r) => r && nav(`/offers/${r.id}/edit`))} data-testid="offer-reuse-btn">
                  <CopyPlus size={15} style={{ verticalAlign: -2 }} /> Genbrug
                </button>
              )}
              {offer.status === "live" && (
                <button className="btn small danger" onClick={() => simpleAction(`/offers/${offer.id}/close`, "Luk for flere bestillinger? Reserverede varer kan stadig hentes.")}>
                  Luk bestilling
                </button>
              )}
              {["draft", "scheduled"].includes(offer.status) && (
                <button className="btn small danger" onClick={del} data-testid="delete-offer">
                  Slet
                </button>
              )}
            </div>
          </section>
          <section className="panel">
            <h2>Aktivitet</h2>
            {d.events.length ? (
              <ul className="feed">
                {d.events.map((ev) => (
                  <li className={`t-${ev.type}`} key={ev.id}>
                    <span><EventText ev={ev} /></span>
                    <time>{relTime(ev.created_at)}</time>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">Intet endnu.</p>
            )}
          </section>
          <details className="panel">
            <summary>
              <b>Opslagets tekst</b>
            </summary>
            <pre style={{ whiteSpace: "pre-wrap", font: "inherit", fontSize: 13, margin: "10px 0 0" }}>{d.post_message}</pre>
          </details>
        </aside>
      </div>
    </>
  );
}
